import { AbstractControl, FormArray, type ValidationErrors, type ValidatorFn } from '@angular/forms';

/**
 * Checks whether two date ranges overlap.
 * Null start means open from the beginning of time.
 * Null end means open until the end of time.
 */
export function rangesOverlap(startA: string | null, endA: string | null, startB: string | null, endB: string | null): boolean {
  const effectiveStartA = startA ?? '';
  const effectiveEndA = endA ?? '\uffff';
  const effectiveStartB = startB ?? '';
  const effectiveEndB = endB ?? '\uffff';
  return effectiveStartA <= effectiveEndB && effectiveStartB <= effectiveEndA;
}

/**
 * A row in a grouped date-range assignment list.
 * The `groupKey` field identifies which group the row belongs to
 * (e.g., skillId, locationId). Rows with the same groupKey are
 * checked for overlapping date ranges.
 */
export interface DateRangeRow {
  id: string;
  groupKey: string;
  start: string | null;
  end: string | null;
}

/**
 * Returns the set of row IDs that have date range conflicts
 * with another row sharing the same groupKey.
 */
export function getConflictingRowIds(rows: DateRangeRow[]): Set<string> {
  const conflicting = new Set<string>();
  const byGroup = new Map<string, DateRangeRow[]>();

  for (const row of rows) {
    if (!row.groupKey) continue;
    const group = byGroup.get(row.groupKey) ?? [];
    group.push(row);
    byGroup.set(row.groupKey, group);
  }

  for (const group of byGroup.values()) {
    if (group.length < 2) continue;
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        if (rangesOverlap(group[i].start, group[i].end, group[j].start, group[j].end)) {
          conflicting.add(group[i].id);
          conflicting.add(group[j].id);
        }
      }
    }
  }

  return conflicting;
}

/**
 * Angular FormArray-level validator that checks for overlapping date ranges
 * within the same group.
 *
 * Each control in the FormArray must have the fields specified by
 * `groupKeyField`, `startField`, and `endField`.
 *
 * When overlaps are detected, sets `{ dateRangeOverlap: true }` on
 * each conflicting control AND returns the error on the FormArray itself.
 *
 * @param groupKeyField  Control name used to group rows (e.g., 'skillId')
 * @param startField     Control name for the range start (e.g., 'acquiredDate')
 * @param endField       Control name for the range end (e.g., 'expiryDate')
 *
 * @example
 * ```typescript
 * const form = new FormArray([], dateRangeOverlapValidator('skillId', 'acquiredDate', 'expiryDate'));
 * ```
 */
export function dateRangeOverlapValidator(groupKeyField: string, startField: string, endField: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormArray)) return null;

    const rows: DateRangeRow[] = [];
    for (let i = 0; i < control.length; i++) {
      const group = control.at(i);
      const groupKey = (group.get(groupKeyField)?.value as string) ?? '';
      if (!groupKey) continue;
      rows.push({
        id: String(i),
        groupKey,
        start: (group.get(startField)?.value as string | null) ?? null,
        end: (group.get(endField)?.value as string | null) ?? null,
      });
    }

    const conflicting = getConflictingRowIds(rows);

    // Clear previous overlap errors, set new ones
    for (let i = 0; i < control.length; i++) {
      const group = control.at(i);
      const errors = group.errors;
      if (conflicting.has(String(i))) {
        group.setErrors({ ...errors, dateRangeOverlap: true });
      } else if (errors?.['dateRangeOverlap']) {
        const { dateRangeOverlap: _, ...rest } = errors;
        group.setErrors(Object.keys(rest).length > 0 ? rest : null);
      }
    }

    return conflicting.size > 0 ? { dateRangeOverlap: true } : null;
  };
}
